import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { cn } from 'cn';
import NProgress from 'nprogress';
import { useEffect, useMemo, useRef } from 'react';
import {
  createBrowserRouter,
  Outlet,
  type RouteObject,
  ScrollRestoration,
  useLocation,
  useMatches,
  useNavigation,
  useParams,
  useRouteError,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { appInsights, appInsightsPlugin } from '#/app-insights';
import { ErrorBoundary } from '#/components/ErrorBoundary/ErrorBoundary';
import Footer from '#/components/Footer/Footer';
import { Header } from '#/components/Header/Header';
import SystemNotice from '#/components/SystemNotice';
import { hasHandle, routes } from '#/routes';
import ErrorPage from '#/routes/ErrorPage';
import PageSpinner from '#/routes/PageSpinner';
import { isErrorResponse, isLoaderError } from './api/errors';
import useCompanyInfo from './api/hooks/useCompanyInfo';
import { useConfig } from './api/hooks/useConfig';
import { useExternalUserInfo } from './api/hooks/useUserInfo';
import VerifyEmailForm from './components/VerifyEmailForm';
import { NavigationLinks } from './routes/NavigationLinks';

function ApplicationShell({
  children,
  showSideNavigation = false,
  userInfo,
  companyInfo,
}: {
  children: React.ReactNode;
  showSideNavigation?: boolean;
  userInfo?: React.ComponentProps<typeof Header>['userInfo'];
  companyInfo?: React.ComponentProps<typeof Header>['companyInfo'];
}) {
  const { data: config } = useConfig();
  const serviceMessage = config?.serviceMessage;

  return (
    <div className="flex min-h-dvh flex-col">
      <div className="sticky top-0 z-50">
        {serviceMessage?.title?.trim() && serviceMessage.message?.trim() ? (
          <SystemNotice
            title={serviceMessage.title}
            message={serviceMessage.message}
            closeable={serviceMessage.closeable ?? false}
          />
        ) : null}
        <Header userInfo={userInfo} companyInfo={companyInfo} />
        {showSideNavigation ? (
          <nav
            role="navigation"
            className="absolute left-0 top-full z-10 hidden h-[calc(100dvh-100%)] w-fit overscroll-none lg:flex lg:max-w-22 lg:justify-center"
          >
            <div
              id="side-navigation"
              className="text-blue-60 group relative h-full w-22 space-y-4 overflow-visible"
            >
              <div
                className={cn(
                  'bg-blue-5 absolute inset-0 z-30 size-full overflow-hidden px-4 pt-4 transition-[width,box-shadow] delay-200 focus-within:delay-0 group-hover:delay-0',
                  'focus-within:shadow-lg-dl-blue group-focus-within:w-64',
                  'group-hover:shadow-lg-dl-blue group-hover:w-64',
                )}
              >
                <NavigationLinks />
              </div>

              <div className="w-22" />
            </div>
          </nav>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function BootstrapShell({ children }: { children: React.ReactNode }) {
  return (
    <ApplicationShell>
      <main role="main" className="relative flex flex-1 flex-col">
        <div className="flex flex-1 flex-col justify-between pl-22">
          <div className="shrink-0 grow">{children}</div>
          <Footer />
        </div>
      </main>
    </ApplicationShell>
  );
}

function Fallback() {
  useEffect(() => {
    let frame: number | undefined = window.requestAnimationFrame(() => {
      if (NProgress.isStarted()) return;
      NProgress.start();
      frame = undefined;
    });

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      } else {
        if (NProgress.status !== null) {
          NProgress.done();
        }
      }
    };
  }, []);

  return (
    <BootstrapShell>
      <PageSpinner />
    </BootstrapShell>
  );
}

function reportError(error: unknown) {
  const exception =
    error instanceof Error
      ? error
      : new Error('Unexpected application error', { cause: error });

  appInsightsPlugin.trackException({
    error: exception,
    exception,
    severityLevel: SeverityLevel.Error,
  });
}

function RouteErrorBoundary() {
  const error = useRouteError();

  let status: number | undefined;

  if (isLoaderError(error)) {
    status = error.errorResponses.find(isErrorResponse)?.status;
  }

  return (
    <Layout>
      <ErrorPage status={status} />
    </Layout>
  );
}

type BootstrapContentProps = Pick<
  React.ComponentProps<typeof Header>,
  'userInfo' | 'companyInfo'
> & {
  hasBootstrapError: boolean;
  isPending: boolean;
};

function BootstrapContent({
  userInfo,
  companyInfo,
  hasBootstrapError,
  isPending,
}: BootstrapContentProps) {
  if (isPending) {
    return <Fallback />;
  }

  if (
    hasBootstrapError &&
    (userInfo === undefined || companyInfo === undefined)
  ) {
    return (
      <BootstrapShell>
        <ErrorPage status={500} />
      </BootstrapShell>
    );
  }

  if (userInfo?.emailVerified === false) {
    return (
      <ApplicationShell userInfo={userInfo}>
        <VerifyEmailForm user={userInfo} companyInfo={companyInfo} />
      </ApplicationShell>
    );
  }

  return <RouterProvider router={getAppRouter()} onError={reportError} />;
}

function Router() {
  const {
    data: userInfo,
    error: userInfoError,
    isPending: isUserInfoPending,
  } = useExternalUserInfo();
  const {
    data: companyInfo,
    error: companyInfoError,
    isPending: isCompanyInfoPending,
  } = useCompanyInfo();
  const bootstrapError = userInfoError ?? companyInfoError;

  useEffect(() => {
    if (bootstrapError) {
      reportError(bootstrapError);
    }
  }, [bootstrapError]);

  return (
    <BootstrapContent
      userInfo={userInfo}
      companyInfo={companyInfo}
      hasBootstrapError={Boolean(bootstrapError)}
      isPending={isUserInfoPending || isCompanyInfoPending}
    />
  );
}

export default Router;

let appRouter: ReturnType<typeof createBrowserRouter> | undefined;

function getAppRouter() {
  if (appRouter) return appRouter;

  appRouter = createBrowserRouter([
    {
      path: '/',
      element: <Layout />,
      HydrateFallback: Fallback,
      ErrorBoundary: RouteErrorBoundary,
      children: [
        ...([...routes] as RouteObject[]),
        {
          path: '*',
          element: <ErrorPage status={404} />,
        },
      ],
    },
  ]);

  return appRouter;
}

function Layout({ children }: { children?: React.ReactNode }) {
  const navigation = useNavigation();
  const { state } = navigation;
  const params = useParams();
  const matches = useMatches();
  const route = matches.at(1);

  const { pathname } = useLocation();
  const prevPath = useRef(pathname);

  const { data: userInfo } = useExternalUserInfo();
  const { data: companyInfo } = useCompanyInfo();

  const showSideNavigation = useMemo(() => {
    if (!route || !hasHandle(route)) return true;
    return route.handle({ params }).hideSideNavigation !== true;
  }, [route, params]);

  useEffect(() => {
    let progressBarFrame: number | null = null;
    if (state === 'loading') {
      const progressBarTimer = window.setTimeout(() => {
        progressBarFrame = window.requestAnimationFrame(() => {
          NProgress.start();
        });
        document.body.classList.add('cursor-wait!');
      }, 150); // do not show progress bar if loading is fast
      return () => {
        if (NProgress.status !== null) {
          NProgress.done();
        } else if (progressBarFrame) {
          window.cancelAnimationFrame(progressBarFrame);
        } else if (progressBarTimer) {
          window.clearTimeout(progressBarTimer);
        }
        document.body.classList.remove('cursor-wait!');
      };
    } else {
      document.body.classList.remove('cursor-wait!');
    }
  }, [state]);

  useEffect(() => {
    appInsights.startTrackPage(pathname);

    return () => {
      appInsights.stopTrackPage(pathname);
    };
  }, [pathname]);

  // Handle focus on main when navigation ends. Waits for loader to finish when loader data is not in cache.
  useEffect(() => {
    const isNavigating = Boolean(navigation.location);
    if (isNavigating === false && pathname !== prevPath.current) {
      document.querySelector('main')?.focus({ preventScroll: true });
      prevPath.current = pathname;
    }
  }, [navigation, pathname]);

  return (
    <ApplicationShell
      showSideNavigation={showSideNavigation}
      userInfo={userInfo}
      companyInfo={companyInfo}
    >
      <ScrollRestoration />
      <div className="relative flex min-h-0 min-w-0 flex-1">
        <div
          className={cn(
            'relative flex min-h-0 min-w-0 w-full max-w-full flex-1',
            {
              'lg:pl-24': showSideNavigation,
            },
          )}
        >
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col justify-between lg:pr-4">
            <main
              role="main"
              tabIndex={-1}
              className="focus:outline-hidden focus:ring-0 lg:mt-4"
            >
              <ErrorBoundary inline={false}>
                <Outlet />
                {children}
              </ErrorBoundary>
            </main>
            <Footer />
          </div>
        </div>
      </div>
    </ApplicationShell>
  );
}
